import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import {
  createLetterhead,
  deleteLetterhead,
  describeSendResult,
  getEventLetterhead,
  letterheadPdfUrl,
  sendLetterhead,
  updateLetterhead,
} from "../Apis/letterhead";
import { EmptyState, LoadingState } from "./ui";

const DEFAULT_TITLE = "Event Items Checklist";

// Same rule as the backend's PDF renderer, so the sheet here matches the printed page.
const LABELLED_LINE = /^([^:]{1,80}):\s+(.+)$/;

// The PDF font covers Latin-1 and a few typographic marks, so Telugu or ₹ would not print.
const UNPRINTABLE = /[^\u0000-ÿ–—‘’“”•…]/;

const phonePattern = /^(?:\+?91)?(\d{10})$/;

// Splits "9876543210, +91 91234 56789" on commas or new lines, ignoring spaces inside a number,
// and flags anything that isn't a mobile number.
function parsePhones(text) {
  const entries = text
    .split(/[,;\n]+/)
    .map((entry) => entry.replace(/\s+/g, ""))
    .filter(Boolean);
  const valid = [];
  const invalid = [];
  entries.forEach((entry) => {
    const match = entry.replace(/[-()]/g, "").match(phonePattern);
    if (match) {
      if (!valid.includes(match[1])) valid.push(match[1]);
    } else {
      invalid.push(entry);
    }
  });
  return { valid, invalid };
}

// The letterhead as it will print: a centred title over a kumkum rule, then the content lines.
export function LetterheadPage({ title, content }) {
  const lines = String(content || "").split(/\r?\n/);
  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="stone.300"
      borderRadius="sm"
      px={{ base: 5, md: 8 }}
      py={{ base: 5, md: 7 }}
      minH="240px"
      boxShadow="0 1px 0 #DFE2E6, 0 3px 0 -1px #FFFFFF, 0 4px 0 -1px #DFE2E6"
    >
      <Text textAlign="center" fontWeight={600} fontSize="lg">
        {title || "Untitled"}
      </Text>
      <Box borderBottom="2px solid" borderColor="kumkum.500" mt={2} mb={4} />
      {lines.map((raw, index) => {
        const line = raw.trim();
        if (!line) return <Box key={index} h={3} />;
        const match = line.match(LABELLED_LINE);
        if (match && !/^https?$/i.test(match[1])) {
          return (
            <Flex key={index} gap={2} mb={1}>
              <Text as="span" color="kumkum.500" aria-hidden="true">
                •
              </Text>
              <Text>
                <Text as="span" fontWeight={600}>
                  {match[1]}:
                </Text>{" "}
                {match[2]}
              </Text>
            </Flex>
          );
        }
        return (
          <Text key={index} mb={1}>
            {line}
          </Text>
        );
      })}
    </Box>
  );
}

function LetterheadSheet({ event, isOpen, onClose }) {
  const toast = useToast();
  const cancelDeleteRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [loadError, setLoadError] = useState("");
  const [letterhead, setLetterhead] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: DEFAULT_TITLE, content: "" });
  const [saving, setSaving] = useState(false);
  const [editorView, setEditorView] = useState("write"); // write | preview
  const [sendOpen, setSendOpen] = useState(false);
  const [phonesText, setPhonesText] = useState("");
  const [sending, setSending] = useState(false);
  // Each send call is a new WhatsApp message, so a ref blocks a second click before state catches up.
  const sendingRef = useRef(false);
  const [sendOutcome, setSendOutcome] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const eventId = event && event._id;

  const load = async () => {
    setStatus("loading");
    setEditing(false);
    try {
      setLetterhead(await getEventLetterhead(eventId));
      setStatus("ready");
    } catch (error) {
      setLoadError(error.message);
      setStatus("error");
    }
  };

  useEffect(() => {
    if (isOpen && eventId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, eventId]);

  const goneElsewhere = () => {
    setLetterhead(null);
    setEditing(false);
    toast({ title: "This letterhead was deleted elsewhere", status: "info", duration: 5000, isClosable: true });
  };

  const startCreate = () => {
    setForm({ title: DEFAULT_TITLE, content: "" });
    setEditorView("write");
    setEditing(true);
  };

  const startEdit = () => {
    setForm({ title: letterhead.title, content: letterhead.content });
    setEditorView("write");
    setEditing(true);
  };

  const canSave = form.title.trim() !== "" && form.content.trim() !== "" && !saving;

  const save = async () => {
    setSaving(true);
    try {
      if (letterhead) {
        const changes = {};
        if (form.title !== letterhead.title) changes.title = form.title;
        if (form.content !== letterhead.content) changes.content = form.content;
        if (Object.keys(changes).length > 0) {
          setLetterhead(await updateLetterhead(letterhead._id, changes));
        }
        toast({ title: "Letterhead saved", status: "success", duration: 3000, isClosable: true });
      } else {
        setLetterhead(await createLetterhead(eventId, form.title, form.content));
        toast({ title: "Letterhead created", status: "success", duration: 3000, isClosable: true });
      }
      setEditing(false);
    } catch (error) {
      if (error.status === 404 && letterhead) {
        goneElsewhere();
      } else {
        toast({ title: "Letterhead not saved", description: error.message, status: "error", duration: 6000, isClosable: true });
      }
    } finally {
      setSaving(false);
    }
  };

  const openSend = () => {
    setPhonesText(event.phone || "");
    setSendOutcome(null);
    setSendOpen(true);
  };

  const phones = parsePhones(phonesText);

  const send = async () => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setSendOutcome(null);
    try {
      const body = await sendLetterhead(letterhead._id, phones.valid);
      setSendOutcome(describeSendResult(body.result));
    } catch (error) {
      if (error.status === 404) {
        setSendOutcome({ sent: false, message: error.message });
      } else {
        setSendOutcome({ sent: false, message: `Send failed: ${error.message}. Try again.` });
      }
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await deleteLetterhead(letterhead._id);
      setLetterhead(null);
      setDeleteOpen(false);
      toast({ title: "Letterhead deleted", status: "success", duration: 3000, isClosable: true });
    } catch (error) {
      toast({ title: "Letterhead not deleted", description: error.message, status: "error", duration: 6000, isClosable: true });
    } finally {
      setDeleting(false);
    }
  };

  const unprintable = UNPRINTABLE.test(form.title + form.content);

  return (
    <>
      <Drawer isOpen={isOpen} onClose={onClose} size={editing ? "xl" : "lg"}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader>
            Letterhead
            <Text fontFamily="body" fontSize="md" color="stone.700" fontWeight={400}>
              {event.fname} {event.lname}, {event.eventName || "Special Event"}, {event.eventDate}
            </Text>
          </DrawerHeader>
          <DrawerCloseButton />
          <DrawerBody>
            {status === "loading" && <LoadingState>Loading letterhead</LoadingState>}

            {status === "error" && (
              <Box>
                <Alert status="error" borderRadius="md" mb={3}>
                  <AlertDescription>Couldn't load the letterhead: {loadError}</AlertDescription>
                </Alert>
                <Button variant="outline" onClick={load}>
                  Try again
                </Button>
              </Box>
            )}

            {status === "ready" && !editing && !letterhead && (
              <EmptyState>
                <Text mb={4}>This event has no letterhead yet.</Text>
                <Button onClick={startCreate}>Create letterhead</Button>
              </EmptyState>
            )}

            {status === "ready" && !editing && letterhead && (
              <LetterheadPage title={letterhead.title} content={letterhead.content} />
            )}

            {status === "ready" && editing && (
              <Box>
                <FormControl mb={4} isRequired>
                  <FormLabel>Title</FormLabel>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                  />
                </FormControl>
                <Flex role="group" aria-label="Editor view" gap={1} mb={2}>
                  {[
                    { key: "write", label: "Write" },
                    { key: "preview", label: "Preview" },
                  ].map((view) => (
                    <Button
                      key={view.key}
                      size="sm"
                      variant={editorView === view.key ? "solid" : "outline"}
                      aria-pressed={editorView === view.key}
                      onClick={() => setEditorView(view.key)}
                    >
                      {view.label}
                    </Button>
                  ))}
                </Flex>
                {editorView === "write" ? (
                  <FormControl isRequired>
                    <FormLabel>Content</FormLabel>
                    <Textarea
                      value={form.content}
                      minH={{ base: "50vh", md: "calc(100vh - 380px)" }}
                      resize="vertical"
                      lineHeight="1.6"
                      placeholder={"Pure Cow Ghee: 2 to 3 kg\nCamphor: 1 big box\n\nPlease keep all items ready one day before the event."}
                      onChange={(e) => setForm((prev) => ({ ...prev, content: e.target.value }))}
                    />
                    <FormHelperText color="stone.700">
                      Lines like "Ghee: 2 kg" print as bold bullet items. Leave a blank line for space.
                    </FormHelperText>
                  </FormControl>
                ) : (
                  <LetterheadPage title={form.title} content={form.content} />
                )}
                {unprintable && (
                  <Alert status="warning" borderRadius="md" mt={3}>
                    <AlertDescription>
                      Some characters won't print, such as Telugu letters or ₹. Write the letterhead in English.
                    </AlertDescription>
                  </Alert>
                )}
              </Box>
            )}
          </DrawerBody>

          {status === "ready" && editing && (
            <DrawerFooter>
              <Button variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button onClick={save} isDisabled={!canSave} isLoading={saving}>
                {letterhead ? "Save letterhead" : "Create letterhead"}
              </Button>
            </DrawerFooter>
          )}

          {status === "ready" && !editing && letterhead && (
            <DrawerFooter justifyContent="space-between" flexWrap="wrap">
              <Button variant="danger" size="sm" onClick={() => setDeleteOpen(true)}>
                Delete
              </Button>
              <Flex gap={2} wrap="wrap">
                <Button variant="outline" size="sm" onClick={startEdit}>
                  Edit
                </Button>
                <Button
                  as="a"
                  href={letterheadPdfUrl(letterhead._id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outline"
                  size="sm"
                >
                  Preview PDF
                </Button>
                <Button size="sm" onClick={openSend}>
                  Send on WhatsApp
                </Button>
              </Flex>
            </DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>

      <Modal isOpen={sendOpen} onClose={() => !sending && setSendOpen(false)} isCentered>
        <ModalOverlay />
        <ModalContent mx={4}>
          <ModalHeader>Send on WhatsApp</ModalHeader>
          <ModalCloseButton isDisabled={sending} />
          <ModalBody>
            <Text mb={3}>Sends the saved letterhead as a PDF, with the customer's name, event and date.</Text>
            <FormControl isInvalid={phones.invalid.length > 0}>
              <FormLabel>Mobile numbers</FormLabel>
              <Input value={phonesText} onChange={(e) => setPhonesText(e.target.value)} isDisabled={sending} />
              <FormHelperText color={phones.invalid.length > 0 ? "#A61B1B" : "stone.700"}>
                {phones.invalid.length > 0
                  ? `Not a 10-digit mobile number: ${phones.invalid.join(", ")}`
                  : "Separate extra numbers with commas."}
              </FormHelperText>
            </FormControl>
            {sendOutcome && (
              <Alert status={sendOutcome.sent ? "success" : "warning"} borderRadius="md" mt={4}>
                <AlertDescription>{sendOutcome.message}</AlertDescription>
              </Alert>
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={() => setSendOpen(false)} isDisabled={sending}>
              {sendOutcome ? "Close" : "Cancel"}
            </Button>
            <Button
              onClick={send}
              isLoading={sending}
              isDisabled={sending || phones.valid.length === 0 || phones.invalid.length > 0}
            >
              {sendOutcome ? "Send again" : "Send"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={deleteOpen} leastDestructiveRef={cancelDeleteRef} onClose={() => setDeleteOpen(false)}>
        <AlertDialogOverlay>
          <AlertDialogContent mx={4}>
            <AlertDialogHeader>Delete this letterhead?</AlertDialogHeader>
            <AlertDialogBody>The event stays. You can create a new letterhead for it later.</AlertDialogBody>
            <AlertDialogFooter gap={2}>
              <Button ref={cancelDeleteRef} variant="ghost" onClick={() => setDeleteOpen(false)}>
                Cancel
              </Button>
              <Button variant="dangerSolid" onClick={confirmDelete} isLoading={deleting}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

export default LetterheadSheet;
